import type {
  GcloudIntegration,
  GcloudSecurityPerimeterComponentKey,
} from "@p0security/integrations-gcloud-shared/types";
import type { GcpWifComponents } from "@p0security/integrations-gcp-wif-shared/components";
import {
  blocksAgenticGrants,
  suggestedDomainAllowPattern,
} from "@p0security/integrations-gcp-wif-shared/perimeter";
import { p0WifPoolTerraform } from "@p0security/integrations-gcp-wif-shared/terraform";
import {
  type FrontendInstallContext,
  type FrontendInstaller,
  integrationOptionProvider,
} from "@p0security/integrations-installer";
import { installedItems } from "@p0security/shared/install/installed";
import { Alert, Typography } from "@p0security/ui";
import { useEffect, useState } from "react";

const { Paragraph, Text } = Typography;

const SECURITY_PERIMETER: GcloudSecurityPerimeterComponentKey =
  "iam-write-security-perimeter";

/** Warns that the security perimeter will reject agentic grants.
 *
 * Advisory only: the perimeter is redeployed outside this integration, so the
 * install can be completed either way.
 */
const PerimeterWarning = (props: { pattern: string }) => {
  const suggested = suggestedDomainAllowPattern(props.pattern);
  return (
    <Alert
      showIcon
      type="warning"
      message="Agentic grants will be rejected by your security perimeter"
      description={
        <>
          <Paragraph>
            Your Google Cloud security perimeter only admits members matching
            its <Text code>DOMAIN_ALLOW_PATTERN</Text>, currently{" "}
            <Text code>{props.pattern}</Text>. That pattern matches user email
            addresses, but agentic access is granted to a Workload Identity
            Federation principal rather than a user, so the perimeter will
            reject those grants and agentic access will fail.
          </Paragraph>
          {suggested ? (
            <>
              <Paragraph>
                You can continue with this installation. To let agentic grants
                through, redeploy the security perimeter with a pattern that
                also admits these principals — for example:
              </Paragraph>
              <Paragraph>
                <Text code>{suggested}</Text>
              </Paragraph>
            </>
          ) : (
            <Paragraph>
              You can continue with this installation. That pattern is not a
              usable regular expression, so we cannot offer a widened version of
              it — correct it first, then redeploy the security perimeter with a
              pattern that also admits <Text code>principal://</Text> members.
            </Paragraph>
          )}
        </>
      }
    />
  );
};

/** Warns using the pattern the perimeter is actually running with.
 *
 * `recorded` is what P0 generated for the setup commands, which the customer
 * may never have deployed or may have since changed, so the deployed value is
 * fetched and preferred. Nothing renders until that resolves: the recorded
 * value is the weaker answer, and flashing a warning that then changes its
 * mind is worse than showing it a moment later. If the fetch fails, the
 * recorded value is all we have.
 */
const PerimeterCheck = (props: {
  context: FrontendInstallContext<unknown>;
  projectId: string;
  recorded: string | undefined;
}) => {
  const { context, projectId, recorded } = props;
  const [pattern, setPattern] = useState<string | undefined>();
  const [resolved, setResolved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      let deployed: string | undefined;
      try {
        const response = await context.authFetch(
          `integrations/gcloud/config/${SECURITY_PERIMETER}/${projectId}`,
          { method: "GET" }
        );
        const body = await response?.json();
        deployed = body?.metadata?.domainAllowPattern;
      } catch {
        deployed = undefined;
      }
      if (cancelled) return;
      setPattern(deployed ?? recorded);
      setResolved(true);
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [context, projectId, recorded]);

  if (!resolved || !pattern || !blocksAgenticGrants(pattern)) return undefined;
  return <PerimeterWarning pattern={pattern} />;
};

/** Renders the warning only when this tenant has a security perimeter whose
 * pattern would reject agentic grants.
 *
 * Reads the gcloud integration's config, since that is where the perimeter
 * lives. Without exactly one installed perimeter there is no veto to warn
 * about -- the same count `installedSecurityPerimeter` requires.
 */
const perimeterPreamble = (
  context: FrontendInstallContext<unknown>
): React.ReactNode => {
  const gcloud = context.installed?.["gcloud"] as GcloudIntegration | undefined;
  if (!gcloud) return undefined;

  const perimeters = Object.entries(installedItems(SECURITY_PERIMETER, gcloud));
  if (perimeters.length !== 1) return undefined;

  // The item is keyed by the project the perimeter is installed in.
  const [projectId, perimeter] = perimeters[0];
  return (
    <PerimeterCheck
      context={context}
      projectId={projectId}
      recorded={perimeter?.allowedDomains}
    />
  );
};

export const gcpWifInstaller: FrontendInstaller<typeof GcpWifComponents> = {
  components: {
    identity: {
      newPreamble: perimeterPreamble,
      instructions: (context, id, item) => ({
        help: (
          <>
            Install this to enable agentic access via Workload Identity
            Federation:
          </>
        ),
        commands: {
          iac: [
            {
              command: p0WifPoolTerraform(context, { item, installName: id }),
            },
          ],
        },
      }),
      items: {
        projectId: {
          optionProvider: integrationOptionProvider("iam-write", [
            { component: "iam-write", integration: "gcloud" },
          ]),
        },
      },
    },
  },
};
